/**
 * @module types/storage
 * @description Storage and persistence type definitions
 *
 * Supports both IndexedDB (preferred) and localStorage (fallback)
 */

import type { Library, LibraryType } from './models';
import type { UIPreferences } from './ui';

// =============================================================================
// INDEXEDDB SCHEMA
// =============================================================================

/** IndexedDB database schema version */
export const DB_VERSION = 1;

/** IndexedDB store names */
export type StoreName = 'settings' | 'cache' | 'searchHistory';

/** Settings store record */
export interface SettingsRecord {
  /** Setting key */
  key: string;
  /** Setting value */
  value: unknown;
  /** Last updated timestamp */
  updatedAt: string;
}

/** Cache store record */
export interface CacheRecord {
  /** Cache key (e.g., 'collections-123-user') */
  key: string;
  /** Cached data */
  data: unknown;
  /** Cache timestamp */
  cachedAt: string;
  /** Expiry timestamp */
  expiresAt: string;
}

/** Search history record */
export interface SearchHistoryRecord {
  /** Search query */
  query: string;
  /** Timestamp */
  searchedAt: string;
  /** Number of results found */
  resultCount?: number;
}

// =============================================================================
// STORED DATA TYPES
// =============================================================================

/** Stored library reference */
export interface StoredLibrary {
  /** Library ID */
  id: number;
  /** Library type */
  type: LibraryType;
  /** Library name (for display) */
  name?: string;
}

/** Stored collection reference */
export interface StoredCollection {
  /** Collection key */
  key: string;
  /** Library ID this collection belongs to */
  libraryId: number;
  /** Library type */
  libraryType: LibraryType;
}

/** Complete stored preferences */
export interface StoredPreferences {
  /** Selected library */
  selectedLibrary?: StoredLibrary;
  /** Selected collection key */
  selectedCollection?: string;
  /** UI preferences */
  ui?: UIPreferences;
  /** Preferred locale */
  locale?: 'en' | 'fr';
  /** Last sync timestamp */
  lastSyncAt?: string;
  /** User item type/field preferences */
  userPreferences?: UserPreferences;
}

// =============================================================================
// USER PREFERENCES (Item Types & Fields)
// =============================================================================

/** User preferences for item types and fields customization */
export interface UserPreferences {
  /** Enabled item types (e.g., ['book', 'journalArticle', 'thesis']) */
  enabledItemTypes: string[];
  /** Fields to display per item type */
  fieldsByItemType: Record<string, string[]>;
  /** Last updated timestamp */
  updatedAt?: string;
}

/** Default user preferences - common academic item types */
export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  enabledItemTypes: [
    'book',
    'bookSection',
    'journalArticle',
    'thesis',
    'conferencePaper',
    'report',
    'webpage',
    'document',
  ],
  fieldsByItemType: {},
};

// =============================================================================
// STORAGE SERVICE INTERFACE
// =============================================================================

/** Storage service interface (for DI) */
export interface IStorageService {
  // Library
  getSelectedLibrary(): StoredLibrary | null;
  setSelectedLibrary(library: StoredLibrary): void;

  // Collection
  getSelectedCollection(): string | null;
  setSelectedCollection(collectionKey: string): void;

  // UI Preferences
  getUIPreferences(): UIPreferences;
  setUIPreferences(prefs: Partial<UIPreferences>): void;

  // User Preferences (Item Types & Fields)
  getUserPreferences(): UserPreferences;
  setUserPreferences(prefs: Partial<UserPreferences>): void;

  // Locale
  getLocale(): 'en' | 'fr';
  setLocale(locale: 'en' | 'fr'): void;

  // Search History
  getSearchHistory(): string[];
  addToSearchHistory(query: string): void;
  clearSearchHistory(): void;

  // Cache
  getCached<T>(key: string): T | null;
  setCached<T>(key: string, data: T, ttlMs?: number): void;
  clearCache(): void;

  // Full clear
  clear(): void;
}

// =============================================================================
// INDEXEDDB SERVICE INTERFACE
// =============================================================================

/** IndexedDB service interface */
export interface IIndexedDBService extends IStorageService {
  /** Initialize the database */
  init(): Promise<void>;

  /** Check if database is ready */
  isReady(): boolean;

  /** Get database instance */
  getDB(): IDBDatabase | null;

  /** Close database connection */
  close(): void;

  /** Export all data (for backup) */
  exportData(): Promise<StoredPreferences>;

  /** Import data (from backup) */
  importData(data: StoredPreferences): Promise<void>;
}

// =============================================================================
// STORAGE KEYS
// =============================================================================

/** Storage key constants (for localStorage fallback) */
export const STORAGE_KEYS = {
  SELECTED_LIBRARY_ID: 'refero-selected-library-id',
  SELECTED_LIBRARY_TYPE: 'refero-selected-library-type',
  SELECTED_LIBRARY_NAME: 'refero-selected-library-name',
  SELECTED_COLLECTION: 'refero-selected-collection',
  UI_PREFERENCES: 'refero-ui-preferences',
  USER_PREFERENCES: 'refero-user-preferences',
  LOCALE: 'refero-locale',
  SEARCH_HISTORY: 'refero-search-history',
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

// =============================================================================
// MIGRATION TYPES
// =============================================================================

/** Database migration function */
export type MigrationFn = (db: IDBDatabase, transaction: IDBTransaction) => void;

/** Migration definition */
export interface Migration {
  /** Target version */
  version: number;
  /** Migration function */
  migrate: MigrationFn;
  /** Description for logging */
  description: string;
}

// =============================================================================
// CACHE CONFIGURATION
// =============================================================================

/** Cache TTL configuration */
export interface CacheConfig {
  /** Default TTL in milliseconds */
  defaultTTL: number;
  /** TTL for collections cache */
  collectionsTTL: number;
  /** TTL for items cache */
  itemsTTL: number;
  /** Maximum cache entries */
  maxEntries: number;
}

/** Default cache configuration */
export const DEFAULT_CACHE_CONFIG: CacheConfig = {
  defaultTTL: 5 * 60 * 1000, // 5 minutes
  collectionsTTL: 10 * 60 * 1000, // 10 minutes
  itemsTTL: 2 * 60 * 1000, // 2 minutes
  maxEntries: 100,
};
